import { usePostFeedbackOnLatestMessageMutation } from "@api/chat";
import { useSendFeedbackMutation } from "@api/quiz";
import {
  HandThumbDownIcon as HandThumbDownIconOutline,
  HandThumbUpIcon as HandThumbUpIconOutline,
} from "@heroicons/react/24/outline";
import {
  HandThumbDownIcon as HandThumbDownIconFilled,
  HandThumbUpIcon as HandThumbUpIconFilled,
} from "@heroicons/react/24/solid";
import { useToastEffect } from "@hooks/hooks";
import {
  MessageFeedbackCommentType,
  QuestionFeedbackCommentType,
  ThumbsFeedbackType,
} from "@onetutor/shared/type";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconWithTooltip } from "./IconWithTooltip";
import { useModal } from "./Modal";

interface FeedbackProps {
  feedbackType: "question" | "message";
  id: string;
  courseId: string;
}

interface ThumbsButtonProps {
  type: "up" | "down";
  isActive: boolean;
  onClick: () => void;
}

const ThumbsButton = ({ type, isActive, onClick }: ThumbsButtonProps) => {
  const { t } = useTranslation("feedback");

  const IconFilled =
    type === "up" ? HandThumbUpIconFilled : HandThumbDownIconFilled;
  const IconOutline =
    type === "up" ? HandThumbUpIconOutline : HandThumbDownIconOutline;

  return (
    <IconWithTooltip
      handler={onClick}
      tooltip={t(`thumbs.${type}`)}
      icon={
        isActive ? (
          <IconFilled className="h-4 w-4 sm:h-5 sm:w-5" />
        ) : (
          <IconOutline className="h-4 w-4 sm:h-5 sm:w-5" />
        )
      }
    />
  );
};

const FeedbackThumbs = ({ feedbackType, id, courseId }: FeedbackProps) => {
  const { t } = useTranslation("feedback");
  const feedbackInputRef = useRef<HTMLInputElement | null>(null);
  const feedbackInputId = useId();

  const { Modal: FeedbackModal, openModal: openFeedbackModal } = useModal();

  const modalTitle =
    feedbackType === "question"
      ? t("questionFeedbackTitle")
      : t("messageFeedbackTitle");
  const tags =
    feedbackType === "question"
      ? Object.values(QuestionFeedbackCommentType)
      : Object.values(MessageFeedbackCommentType);

  const [isThumbsUp, setIsThumbsUp] = useState(false);
  const [isThumbsDown, setIsThumbsDown] = useState(false);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  const [
    sendFeedbackQuestion,
    { isError: isQuestionError, error: questionErr },
  ] = useSendFeedbackMutation();
  const [sendFeedbackMessage, { isError: isMessageError, error: messageErr }] =
    usePostFeedbackOnLatestMessageMutation();

  const isError =
    feedbackType === "question" ? isQuestionError : isMessageError;
  const error = feedbackType === "question" ? questionErr : messageErr;

  useToastEffect({ isError, error });

  const handleFeedback = async (feedback: ThumbsFeedbackType) => {
    if (feedback === ThumbsFeedbackType.THUMBS_UP) {
      if (isThumbsUp) {
        return;
      }
      setIsThumbsDown(false);
      if (feedbackType === "question") {
        await sendFeedbackQuestion({
          courseId,
          questionId: id,
          feedback: { feedback },
        });
      } else {
        await sendFeedbackMessage({
          courseId: courseId || "",
          sessionId: id,
          feedback: { feedback },
        });
      }
      setIsThumbsUp(true);
      return;
    }
    openFeedbackModal();
  };

  const handleConfirm = async () => {
    setIsThumbsUp(false);
    if (feedbackType === "question") {
      await sendFeedbackQuestion({
        courseId,
        questionId: id,
        feedback: {
          feedback: ThumbsFeedbackType.THUMBS_DOWN,
          comment: selectedTag || undefined,
        },
      });
    } else {
      await sendFeedbackMessage({
        courseId: courseId || "",
        sessionId: id,
        feedback: {
          feedback: ThumbsFeedbackType.THUMBS_DOWN,
          comment: selectedTag || undefined,
        },
      });
    }
    setIsThumbsDown(true);
  };

  const handleTagClick = (tag: string) => {
    setSelectedTag(tag);
    if (feedbackInputRef.current) {
      feedbackInputRef.current.value = "";
    }
  };

  return (
    <div className="flex gap-1">
      <ThumbsButton
        type="up"
        isActive={isThumbsUp}
        onClick={() => handleFeedback(ThumbsFeedbackType.THUMBS_UP)}
      />
      <ThumbsButton
        type="down"
        isActive={isThumbsDown}
        onClick={() => handleFeedback(ThumbsFeedbackType.THUMBS_DOWN)}
      />
      <FeedbackModal
        title={modalTitle}
        onConfirm={handleConfirm}
        onConfirmLabel={t("form.sendFeedback")}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <button
                key={tag}
                className={`btn btn-sm btn-primary ${
                  selectedTag === tag ? "" : "btn-outline"
                }`}
                onClick={() => handleTagClick(tag)}
                type="button"
              >
                {t(`type.${tag}`)}
              </button>
            ))}
          </div>
          <label htmlFor={feedbackInputId}>
            <span className="font-semibold text-lg">{t("type.custom")}</span>
          </label>
          <input
            type="text"
            id={feedbackInputId}
            ref={feedbackInputRef}
            placeholder={
              selectedTag ? t(`type.${selectedTag}`) : t("type.custom")
            }
            onChange={(e) => {
              const value = e.target.value;
              if (value.trim() !== "") {
                setSelectedTag(value);
              }
            }}
            className="input w-full"
          />
        </div>
      </FeedbackModal>
    </div>
  );
};

export default FeedbackThumbs;
